/**
 * Module based on Lea Verou's Awesomplete, http://leaverou.github.io/awesomplete a simple, lightweight, usable local autocomplete library for modern browsers
 * @author Serge Huijben
 * MIT license
 *
 * autosuggestUrl should be like 'https://autosuggest.iqnomy.com/autosuggest/api/suggest/dictionary/'
 */
import { registerTriggers, triggerNotify } from "./@helpers/js/observer.js";


// Helpers

const slice = Array.prototype.slice;

const $ = (expr, con) => typeof expr === 'string' ? (con || document).querySelector(expr) : expr || null;

const $$ = (expr, con) => slice.call((con || document).querySelectorAll(expr));

$.create = (tag, o) => {
    const element = document.createElement(tag);

    for (let i in o) {
        let val = o[i];

        if (i === 'inside') {
            $(val).appendChild(element);
        } else if (i === 'after') {
            let afterref = $(val);
            afterref.parentNode.insertBefore(element, afterref.nextSibling);
        } else if (i === 'around') {
            let ref = $(val);
            ref.parentNode.insertBefore(element, ref);
            element.appendChild(ref);
        } else if (i in element) {
            element[i] = val;
        } else {
            element.setAttribute(i, val);
        }
    }

    return element;
};

$.getQueryString = (name, raw = false) => {
    if (!name || typeof name !== 'string') {
        return '';
    }
    let params = location.search && location.search.length > 0 && location.search.indexOf(name) > -1 ? location.search.substring(1).split("&") :
        location.hash && location.hash.length > 0 && location.hash.indexOf(name) > -1 ? location.hash.substring(1).split("&") : [];

    for(let i = 0; i < params.length; i++)
    {
        params[i] = params[i].replace(/\+/g, ' ');
        
    }

    for (let param of params) {
        const p = param.split('=');
        if (p[0]?.toLowerCase() === name.toLowerCase())
            return raw ? p[1] : decodeURIComponent(p[1]);
    }
    return '';
}

$.regExpEscape = (s) => s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');

$.siblingIndex = (el) => {
    /* eslint-disable no-cond-assign */
    let i = 0;
    while (el.previousElementSibling) {
        el = el.previousElementSibling;
        i++;
    }
    return i;
};

// Static methods/properties
const FILTER_CONTAINS = (text, input) => new RegExp($.regExpEscape(input.trim()), 'i').test(text);

const FILTER_STARTSWITH = (text, input) => new RegExp('^' + $.regExpEscape(input.trim()), 'i').test(text);

const SORT_BYLENGTH = (a, b) => (a.length !== b.length) ? a.length - b.length : a < b ? -1 : 1;

const MARKTEXT = (text, input) => input === '' ? text : text.replace(new RegExp($.regExpEscape(input.trim()), 'gi'), '<mark>$&</mark>');

const ITEM = (html) => `<li aria-selected="false">${html}</li>`;

const MAKETEXT = (text, href) => !href ? text : `<a href="${href}" class="c-link">${text}</a>`;

class SearchAwesomplete {

    constructor(element, {
        caption = 'Suggestions',
        autosuggestUrl = '',
        method = 'GET',
        key = 'key',
        autosuggestKey = 'query',
        autofocus = false,
        sort = true,
        filter = false,
        minChars = 2,
        maxItems = 10,
        autoFirst = false
    } = {}) {
        this._options = {
            caption,
            autosuggestUrl,
            method,
            key,
            autosuggestKey,
            autofocus,
            sort,
            filter,
            minChars,
            maxItems,
            autoFirst
        };
        this._element = element;
        this.input = $(this._element.querySelector('input.c-search-awesomplete__input'));
        this.submitButton = $(this._element.querySelector('[data-search-awesomplete-button]')) // use this attribute only if you want autosuggest on button click
        this.autosuggestDictionary = $(this._element.querySelector('.c-search-awesomplete__hiddendictionary'));
        this.notFoundTemplate = $(this._element.querySelector('[data-awesomplete-404]'));

        if (this.input) {
            this._init();
        }
    }

    // Setup
    _init() {
        this.input.setAttribute('autocomplete', 'off');
        this.input.setAttribute('aria-autocomplete', 'list');
        this.input.value = $.getQueryString(this._options.key);

        this.filter = FILTER_CONTAINS;
        this.sort = SORT_BYLENGTH;
        this.item = ITEM;
        this.makeText = MAKETEXT;

        this.index = -1;

        this._createElements();

        this._bindEvents();

        if (this.input.hasAttribute('list')) {
            this.list = '#' + this.input.getAttribute('list');
            this.input.removeAttribute('list');
        } else {
            this.list = this.input.getAttribute('data-list') || this._options.list || [];
        }

        if (this.input.value && this._options.autofocus === true)
            window.setTimeout(()=>this.input.focus(),100);
        registerTriggers([
            "awesomplete-highlight",
            "awesomplete-close",
            "awesomplete-open",
            "awesomplete-select",
            "awesomplete-selectcomplete",
        ]);
    }

    // Create necessary elements
    _createElements() {

        this.container = $.create('div', {
            className: 'awesomplete',
            around: this.input
        });

        this.innerContainer = this._element.tagName === 'FORM' ? $.create('div', {
            className: 'c-search-awesomplete__list',
            hidden: 'hidden',
            inside: this.container
        }) : $.create('div', {
            className: 'c-search-awesomplete__list',
            hidden: 'hidden',
            after: $(this._element.querySelector('form.c-search-form__querybox'))
        });

        this.caption = $.create('span', {
            className: 'caption',
            inside: this.innerContainer
        });
        this.caption.innerHTML = this._options.caption;

        this.ul = $.create('ul', {
            inside: this.innerContainer
        });

        this.notFound = $.create('div', {
            className: 'c-search-awesomplete__list-404',
            inside: this.innerContainer
        });

        this.status = $.create('span', {
            className: 't-visuallyhidden',
            role: 'status',
            'aria-live': 'assertive',
            'aria-atomic': 'true',
            'aria-relevant': 'additions',
            inside: this.container
        });

    }

    // Bind events
    _bindEvents() {

        if (this.input) {
            ['click','touchend','focus'].forEach((event) => {
                this.input.addEventListener(event, () => {
                    if (this.timeoutID && typeof this.timeoutID === 'number') {
                        window.clearTimeout(this.timeoutID);
                    }
                    this.timeoutID = window.setTimeout(() => {
                        if (this.inputValueBackup && this.input.value && this.input.value === this.inputValueBackup && ((this.notFoundBackup && this.notFoundBackup.length > 0) || (this.ulBackup && this.ulBackup.length > 0))) {
                            this.ul.innerHTML = this.ulBackup;
                            this.notFound.innerHTML = this.notFoundBackup;
                            this.open();
                        } else if (!this.input.value || this.input.value.length < this._options.minChars) {
                            this.close({reason:'clear'});
                        } else {
                            this.evaluate();
                        }
                    },200);
                });
            });
            this.input.addEventListener('input', () => this.wrapEvaluation());
            this.input.addEventListener('blur', () => this.close({reason: 'blur'}));
            this.input.addEventListener('keydown', (evt) => {
                const c = evt.keyCode;

                // If the dropdown `ul` is in view, then act on keydown for the following keys:
                // Enter / Esc / Up / Down
                if (this.opened) {
                    if (c === 13) { // Enter
                        if (this.selected) {
                            evt.preventDefault();
                            this.select();
                        } else {
                            let count = this.ul.children.length;
                            if (count <= 0) {
                                let linkToSearch = this.notFound.querySelector('.c-link');
                                if (linkToSearch && linkToSearch.getAttribute('aria-selected') && linkToSearch.getAttribute('aria-selected') === 'true' && linkToSearch.href) {
                                    history.replaceState({foo:'bar'}, "", `${window.location.href.split('?')[0]}?${linkToSearch.href.split('?')[1] ? linkToSearch.href.split('?')[1] : '' }`);
                                    window.location = linkToSearch.href;
                                    this.close({
                                        reason: 'link'
                                    });
                                }
                            }
                        }
                    } else if (c === 27) { // Esc
                        this.close({
                            reason: 'esc'
                        });
                    } else if (c === 38 || c === 40) { // Down/Up arrow
                        evt.preventDefault();
                        let count = this.ul.children.length;
                        if (count <= 0) {
                            let linkToSearch = this.notFound.querySelector('.c-link');
                            if (linkToSearch) {
                                let toggle = linkToSearch.getAttribute('aria-selected') && linkToSearch.getAttribute('aria-selected') === 'true';
                                linkToSearch.setAttribute('aria-selected', !toggle);
                            }
                        } else {
                            this[c === 38 ? 'previous' : 'next'](count);
                        }
                    }
                }
            });
            const forbiddenChars = /[<>{}[\]$"'/]/g;
            // Act on keypress and paste action for the following keys:
            // < > ' " $ { } [ ] /  keys
            this.input.addEventListener('input', (evt) => {
                this.input.value = this.input.value.replace(forbiddenChars, '')
                evt.preventDefault();
            });
        }

        if (this.submitButton) {
            this.submitButton.addEventListener('click', (evt) => {
                evt.preventDefault();
                this.evaluate(true);
            });
        }

        if (this.input && this.input.form) {
            this.input.form.addEventListener('submit', () => this.close({reason: 'submit'}));
        }

        if (this.ul) {
            this.ul.addEventListener('mousedown', (evt) => {
                let li = evt.target;

                if (li !== this) {

                    while (li && !/li/i.test(li.nodeName)) {
                        li = li.parentNode;
                    }

                    if (li && evt.button === 0) { // Only select on left click
                        evt.preventDefault();
                        this.select(li, evt.target);
                    }
                }
            });
        }
    }

    set list(list) {
        if (Array.isArray(list)) {
            this._list = list;
        } else if (typeof list === 'string' && list.indexOf(',') > -1) {
            this._list = list.split(/\s*,\s*/);
        } else { // Element or CSS selector
            list = $(list);

            if (list && list.children) {
                const items = [];
                slice.apply(list.children).forEach(function(el) {
                    if (!el.disabled) {
                        const text = el.textContent.trim();
                        const value = el.value || text;
                        const label = el.label || text;
                        if (value !== '') {
                            items.push({
                                label: label,
                                value: value
                            });
                        }
                    }
                });
                this._list = items;
            }
        }

        if (document.activeElement === this.input) {
            this.wrapEvaluation();
        }
    }

    get list() {
        return this._list;
    }

    get selected() {
        return this.index > -1;
    }

    get opened() {
        return !this.innerContainer.hasAttribute('hidden');
    }

    close(o) {
        if (!this.opened) {
            return;
        }
        const ms = o && o.reason && o.reason === 'clear' ? 10 : 200;
        window.setTimeout(() => {
            this.innerContainer.setAttribute('hidden', '');
            if (this.input.value && this.input.value.length >= this._options.minChars) {
                this.inputValueBackup = `${this.input.value}`;
                this.notFoundBackup = `${this.notFound.innerHTML}`;
                this.ulBackup = `${this.ul.innerHTML}`;
            }
            this.notFound.innerHTML = '';
            this.ul.innerHTML = '';
        }, ms);
        this.index = -1;

        triggerNotify('awesomplete-close', o || {});
    }

    open() {
        this.innerContainer.removeAttribute('hidden');

        if (this._options.autoFirst && this.index === -1) {
            this.goto(0);
        }

        triggerNotify('awesomplete-open');
    }

    next(count) {
        this.goto(this.index < count - 1 ? this.index + 1 : -1);
    }

    previous(count) {
        this.goto(this.selected ? this.index - 1 : count - 1);
    }

    // Should not be used, highlights specific item without any checks!
    goto(i) {
        const lis = this.ul.children;

        if (this.selected) {
            lis[this.index].setAttribute('aria-selected', 'false');
        }

        this.index = i;

        if (i > -1 && lis.length > 0) {
            lis[i].setAttribute('aria-selected', 'true');
            this.status.textContent = lis[i].textContent;

            triggerNotify('awesomplete-highlight', {
                text: this.suggestions[this.index]
            });
        }
    }

    select(selected, origin) {
        if (selected) {
            this.index = $.siblingIndex(selected);
        } else {
            selected = this.ul.children[this.index];
        }

        if (selected) {
            const suggestion = this.suggestions[this.index];
            if (suggestion.href) {
                const searchable = `${this._options.key}=${$.getQueryString(this._options.key, true)}`;
                if (window.location.href.indexOf(searchable) === -1) {
                    history.replaceState({foo:'bar'}, "", `${window.location.href.indexOf('?') > -1 ? window.location.href + '&' : window.location.href + '?'}${this._options.key}=${this.input.value}`);
                } else if (this.input.value !== $.getQueryString(this._options.key)) {
                    history.replaceState({foo:'bar'}, "", window.location.href.replace(searchable, `${this._options.key}=${this.input.value}`));
                }
                window.location = suggestion.href;
                this.close({
                    reason: 'link'
                });
            } else {
                const allowed = triggerNotify('awesomplete-select', {
                    text: suggestion,
                    origin: origin || selected
                });

                if (allowed) {
                    this.input.value = suggestion.label?suggestion.label.replace(/<mark>/g,'').replace(/<\/mark>/g,''):'';
                    this.close({
                        reason: 'select'
                    });
                    triggerNotify('awesomplete-selectcomplete', {
                        text: suggestion
                    });
                    if (window.$(this._element).is('form')) {
                        window.$(this._element).submit();
                    } else {
                        window.$(this._element).find('form').submit();
                    }
                }
            }
        }
    }

    wrapEvaluation() {
        if (this.timeoutID && typeof this.timeoutID === 'number') {
            window.clearTimeout(this.timeoutID);
        }
        this.timeoutID = window.setTimeout(() => this.evaluate(), 400);
    }

    noSuggestionsFound(value) {
        if (this.notFoundTemplate && this.notFoundTemplate.innerHTML && this.notFoundTemplate.innerHTML.length > 0) {
            this.notFound.innerHTML = this.notFoundTemplate.innerHTML.replace(/\{0\}/g, value);
        }
        return !!(this.notFound?.innerHTML?.length > 0);
    }

    async evaluate(skipLengthCheck) {
        this.inputValueBackup = '';
        const value = this.input.value;
        const controller = (window.AbortController) ? new AbortController() : {};
        const signal = controller.signal || {};

        let fetchPromise;
        if ((skipLengthCheck && value.length > 0)|| value.length >= this._options.minChars) {
            if (this._options.autosuggestUrl) {
                this.notFound.innerHTML = '';
                if (fetchPromise && controller.abort) {
                    controller.abort();
                }
                let suggestURL = '';
                let searchData = {};
                let key = this._options.key ? this._options.key : 'key';
                if (this._options.method === "POST") {
                    suggestURL = `${this._options.autosuggestUrl}${((this.autosuggestDictionary) ? (this.autosuggestDictionary.value) ? this.autosuggestDictionary.value : 'cz-all' : '')}`;
                    
                    // Create data object
                    searchData = {
                        "maxResults": this._options.maxResults
                    };
                    // Add key value pair to data object
                    searchData[key] = value;
                } else {
                    suggestURL = `${this._options.autosuggestUrl}&${this._options.autosuggestKey}=${value}`;
                }    
                    try {
                        fetchPromise = fetch(suggestURL, {
                            signal,
                            method : this._options.method || "GET",
                            headers: {
                                "Content-Type": "application/json; charset=utf-8"
                            },
                            body: this._options.method === "POST" ? JSON.stringify(searchData) : undefined
                        });
                        let response = await fetchPromise;
                        let result = await response.json();
						this.ul.innerHTML = '';
                
                        if (result) {
                            this._list = result;
                            if (this._list.length > 0) {
                                this.index = -1;
                                // Populate list with options that match
                                this.suggestions = this._list
                                    .map((item) => new Suggestion(item))
                                    .filter((item) => {
                                        return this._options.filter ? this.filter(item.label,value): true;
                                    })
                                    .sort((a,b) => {
                                        return this._options.sort ? this.sort(a,b): 0
                                    })
                                    .slice(0, this._options.maxItems);

                                let html = this.suggestions.reduce((sentence, suggestion) => `${sentence}${ITEM( MAKETEXT( MARKTEXT( suggestion.label, value ), suggestion.href ))}`, '')

                                this.ul.innerHTML = html;

                                if (this.ul.children.length === 0) {
                                    if (this.noSuggestionsFound(value)) {
                                        this.open();
                                    } else {
                                        this.close({
                                            reason: 'nomatches'
                                        });
                                    }
                                } else {
                                    this.open();
                                }
                            } else {
                                if (this.noSuggestionsFound(value)) {
                                    this.open();
                                } else {
                                    this.close({
                                        reason: 'nomatches'
                                    });
                                }
                            }
                        } else {
                            console.error('server returns wrong status');
                            if (this.noSuggestionsFound(value)) {
                                this.open();
                            } else {
                                this.close({
                                    reason: 'nomatches'
                                });
                            }
                    }

                    } catch (error) {
                        this.ul.innerHTML = '';
						console.error(error);
                        if (this.noSuggestionsFound(value)) {
                            this.open();
                        } else {
                            this.close({
                                reason: 'error'
                            });
                        }
                    } finally {
                        fetchPromise = undefined;
                    }
            } else {
                this.close({
                    reason: 'no autosuggest url'
                });
            }
        } else {
            this.close({
                reason: 'to little characters'
            });
        }
    }
};

// Private class
class Suggestion {
    constructor(data) {
        const o = Array.isArray(data) ? {
            label: data[0],
            value: data[1]
        } : typeof data === 'object' && 'text' in data && 'value' in data ? data : {
            label: data,
            value: data
        };

        this.label = o.text || o.label || '';
        this.href = o.href || '';
        this.value = o.value;
    }

    get length() {
        return this.label.length;
    }

    toString() {
        return '' + this.label;
    }
}

export default SearchAwesomplete; // as SearchAwesomplete
